import { fetcher } from "@website/fetcher/fetcher";
import { getChannel } from "@website/utils/channel/get-channel";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isChannel } from "@website/utils/channel/helpers";
import {
  daysBetweenDates,
  isInvalidCheckin,
  today,
  tomorrow
} from "@website/utils/date-format";
import { replaceOptions } from "@website/utils/replace-options";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import get from "lodash-es/get";
import omit from "lodash-es/omit";
import pick from "lodash-es/pick";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import type from "markdown";
import { GetServerSideProps } from "next";
import { filterXSS } from "xss";
import { ENDPOINTS } from "../../../constants";
import { fetchHotelsData, fetchSeoData } from "../index.server";
import { HotelDomesticType } from "../index.types";
import {
  HotelDomesticCalendarType,
  HotelDomesticHotelDataType,
  HotelDomesticRateDataType,
  HotelDomesticReviewDataType,
  HotelDomesticRoomType,
  HotelDomesticRoomsType,
  PersianDomesticRatings
} from "./index.types";

const static_policies = [
  `در صورت کنسلی رزرو، علاوه بر جریمه اعلامی توسط هتل، ۱٪ از مبلغ کل رزرو بابت هزینه خدمات کنسلی توسط ${replaceSnappTripBasedOnChannel(
    "اسنپ‌تریپ"
  )} کسر خواهد شد.`
];

export const fetchHotelData = (cityTitle: unknown, hotelTitle: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_INFO,
    options: { hotelTitle, cityTitle },
    method: "GET",
    params: { token: "Jek" },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.HOTEL_INFO, toString(cityTitle), toString(hotelTitle)]
    }
  }).then(
    ({ data }) =>
      ({
        ...omit(data, [
          "privacy",
          "policy",
          "snapp_description",
          "cancellation_policy"
        ]),
        gallery:
          data.gallery?.map((item) => ({
            ...item,
            title: replaceSnappTripBasedOnChannel(item.title)
          })) ?? [],
        seo_title: replaceSnappTripBasedOnChannel(data.seo_title) ?? "",
        seo_meta_desc: replaceSnappTripBasedOnChannel(data.seo_meta_desc) ?? "",
        categories: data.categories?.map((item) => ({
          ...item,
          name: replaceSnappTripBasedOnChannel(item.name),
          main_category_type: replaceSnappTripBasedOnChannel(
            item.main_category_type
          )
        })),
        badges:
          data?.badges?.map((item) => ({
            ...item,
            title: replaceSnappTripBasedOnChannel(item.title),
            description: replaceSnappTripBasedOnChannel(item.description)
          })) || [],
        description:
          getChannel() === "snapp-ir"
            ? data?.snapp_description
            : filterXSS(data?.description, {
                css: false,
                whiteList: {
                  p: [],
                  br: [],
                  ul: [],
                  li: [],
                  strong: [],
                  ...(isChannel(["website", "web"])
                    ? { a: ["href", "title", "target"] }
                    : {})
                },
                stripIgnoreTag: true,
                stripIgnoreTagBody: ["script"]
              }),
        hotel_policy: replaceSnappTripBasedOnChannel(
          filterXSS(data?.privacy, {
            whiteList: {},
            stripIgnoreTag: true,
            stripIgnoreTagBody: ["script"]
          })
        ),
        cancellation_policy: replaceSnappTripBasedOnChannel(
          filterXSS(data?.cancellation_policy + static_policies.join(","), {
            whiteList: {},
            stripIgnoreTag: true,
            stripIgnoreTagBody: ["script"]
          })
        )
      }) as HotelDomesticHotelDataType
  );

export const fetchHotelRoomsData = (hotelId: unknown, query: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_ROOMS,
    options: { hotelId },
    params: {
      ...pick(query, "date_from", "date_to"),
      token: "Jek"
    },
    method: "GET"
  }).then(
    ({ data }) =>
      ((data ?? []) as HotelDomesticRoomsType)?.map((room) => ({
        id: room.room_id,
        roomTypeId: room.room_type_id,
        roomTypeTitle: room?.room_type_title?.replace("جدید", ""),
        hotelId: room.hotel_id,
        bookNeedConfirm: room.book_need_confirm,
        bookType: room.booking_type,
        promotion: Math.ceil(room.promotion.average_discount_percent),
        provider: room.provider,
        adults: room.adults,
        title: room.title,
        availableRooms: room.available_rooms,
        minStay: toNumber(room?.min_stay) || 1,
        roomCount: toNumber(get(query, "no_rooms")) || 1,
        extraBed: room.prices.extra_bed_price > 0 ? room.extra_bed : 0,
        hasBreakfast: room.breakfast_included,
        facilities: room.facilities_tags.map((tag) => ({
          ...tag,
          icon: tag.icon === "what.ico" ? "check_box" : tag.icon
        })),
        description: room.description,
        price: room.prices.local_price,
        priceOff: room.prices.local_price_off,
        basePrice: room.prices.base_price,
        originalSellPrice: room.prices.original_sell_price,
        childPrice: room.prices.child_final_price,
        hasSnappProDiscount: room.snapp_pro_discount,
        ruleId: room.rule_id,
        regularBadges: [
          ...("pe_badge_title" in room &&
          room.pe_badge_title?.length &&
          room.available_rooms
            ? [
                {
                  type: room.pe_badge_title,
                  color: {
                    last_minute: "primary" as const
                  }[room.pe_badge_title],
                  text: {
                    last_minute: "تخفیف لحظه آخری"
                  }[room.pe_badge_title],
                  icon: {
                    last_minute: "timer" as const
                  }[room.pe_badge_title],
                  transparent: false
                }
              ]
            : []),
          ...((
            room?.badges?.filter(
              ({ type }) => type === "ordinary"
            ) as HotelDomesticType["badges"]
          )?.map(({ color, title }) => ({
            color,
            text: title,
            transparent: false
          })) ?? [])
        ],
        rackIncluded:
          room?.racks?.length > 0 &&
          room?.racks?.some((rack) => rack?.availability),
        roomRacks:
          room?.racks
            ?.map((rack) => ({
              availableRoom: rack?.availability,
              bookingType: room?.booking_type,
              dateFrom: rack?.from,
              dateTo: rack?.to,
              price: rack?.price,
              priceOff: rack?.price_off,
              nightCount: daysBetweenDates(rack?.from, rack?.to),
              hotelId: room?.hotel_id,
              roomId: room?.room_id
            }))
            ?.filter((rack) => rack?.availableRoom) ?? [],
        img: `${ENDPOINTS.CDN_IMAGES}/346x168${room.cover_media.media}`,
        nightCount:
          daysBetweenDates(get(query, "date_from"), get(query, "date_to")) || 1
      }))
  );

export const fetchHotelPriceRange = (hotelId: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_PRICE_RANGE,
    options: { hotelId },
    params: {
      token: "Jek"
    },
    method: "GET",
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.HOTEL_PRICE_RANGE, toString(hotelId)]
    }
  })
    .then(({ data }) => data)
    .catch(() => ({ min: 0, max: 15_500_000 }));

export const fetchHotelRateData = (hotelId: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_RATE,
    options: { hotelId },
    method: "GET",
    headers: {
      product: "hotel-domestic"
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.HOTEL_RATE, toString(hotelId)]
    }
  }).then(({ data }) => {
    const _data = data as HotelDomesticRateDataType;
    return {
      rateDetail: {
        clean_rate: _data?.cleaning || "",
        facility_rate: _data?.facilities || "",
        food_quality_rate: _data?.food || "",
        location_rate: _data?.location || "",
        staff_rate: _data?.friendly || "",
        value_for_money_rate: _data?.priceQuality || ""
      },
      rate: Number(_data?.overall).toFixed(1) ?? "",
      rateCount: _data?.rateCount ?? 0,
      offerCount: _data?.recommendationCount ?? 0,
      offerMessage:
        _data?.recommendationType === "recommendation"
          ? "۷۵ درصد از میهمانان اخیر، این اقامتگاه رو پیشنهاد کرده اند. "
          : _data?.recommendationType === "fullRate"
            ? "میهمانان در ۵ رزرو اخیر، امتیاز کامل را به اقامتگاه داده اند"
            : _data?.recommendationType
              ? `میهمانان در ۵ رزرو اخیر، به  "${PersianDomesticRatings?.[
                  _data?.recommendationType
                ]}" امتیاز کامل داده اند.`
              : "",
      aiSummary: type?.markdown?.toHTML(data?.aiSummary)
    };
  });
export const fetchHotelReviewData = (
  hotelId: unknown,
  page: unknown,
  product: string,
  filters: Record<string, string>,
  roomId?: unknown,
  order?: string
) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW, { hotelId }),
    method: "GET",
    headers: {
      product
    },
    params: {
      ...filters,
      order_by: order || "default",
      roomId: roomId || "",
      pageSize: 5,
      page
    }
  }).then(({ data }) => {
    const _data = data as HotelDomesticReviewDataType;
    return {
      page: toNumber(_data?.page),
      total: _data?.total,
      rateReviews: (_data?.result ?? [])?.map((comment) => ({
        id: comment?.id,
        helpfulSubmitted: comment?.helpfulSubmitted,
        userName: comment?.passenger,
        roomName: comment.roomTitle,
        rate: String(comment?.avgRating || 0),
        registeredDate: comment?.createdAt,
        standNights: comment?.nights || comment?.dateCount || 1,
        roomId: comment?.roomId,
        createdAt: comment?.createdAt,
        comment:
          replaceSnappTripBasedOnChannel(
            filterXSS(comment?.comment, {
              whiteList: {},
              stripIgnoreTag: true,
              stripIgnoreTagBody: ["script"]
            })
          ) ?? "",
        strengths: comment?.pros?.filter(Boolean)?.join(" ,") ?? "",
        weakness: comment?.cons?.filter(Boolean)?.join(" ,") ?? "",
        source: comment?.source ?? "",
        pictures: comment?.images?.map((image, index) => ({
          id: index,
          media: `${process.env["NEXT_PUBLIC_BASE_URL_STORE"]}/reviews/${image}`,
          title: ""
        }))
      }))
    };
  });

export const fetchUsefulnessReviewData = (reviewId: number) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW_USEFULNESS, { reviewId }),
    method: "POST"
  }).then(() => {});

export const fetchHotelCalendarData = (
  hotelId: HotelDomesticRoomType["hotel_id"],
  roomId?: HotelDomesticRoomType["room_id"],
  locale?: string,
  query?: object
) =>
  fetcher({
    url: ENDPOINTS.GET_ROOM_CALENDAR,
    options: { roomId, hotelId },
    params: {
      limit: 36,
      lang: locale,
      ...(isInvalidCheckin(
        get(query, "date_from"),
        get(query, "date_to"),
        false
      )
        ? {
            date_from: today(),
            date_to: tomorrow()
          }
        : pick(query, ["date_from", "date_to"]))
    }
  }).then(({ data }) =>
    ((data?.data?.[0].calendar ?? []) as Array<HotelDomesticCalendarType>).map(
      ({ date, prices, available_rooms }) => ({
        date: date,
        price: prices?.local_price,
        priceOff: prices?.local_price_off,
        isAvailable: available_rooms >= 1
      })
    )
  );

export const fetchInstalmentWallet = (): Promise<{
  isEligible: boolean;
  showDownPayment: boolean;
  markup: number;
}> =>
  fetcher({
    url: ENDPOINTS.INSTALMENT_WALLET,
    method: "GET",
    headers: {
      product: "HOTEL"
    }
  }).then(({ data }) => ({
    isEligible: data?.is_eligible,
    showDownPayment: data?.show_down_payment,
    markup: data?.markup
  }));

export const fetchRoomsNotify = (data: { hotelId: number }) =>
  fetcher({
    url: ENDPOINTS.ROOM_NOTIFY,
    method: "GET",
    headers: {
      product: "HOTEL-DOMESTIC"
    },
    params: {
      property_id: data?.hotelId
    }
  }).then(
    ({ data }) =>
      data?.map((item) => ({
        expireDate: item?.expires_at,
        dateFrom: item?.from,
        hotelId: item?.property_id,
        roomId: item?.room_id,
        dateTo: item?.to,
        typ: item?.type
      }))
  );

export const fetchAddRoomNotify = (data: {
  dateFrom: string;
  hotelId: number;
  roomId: number;
  dateTo: string;
}) =>
  fetcher({
    url: ENDPOINTS.ROOM_NOTIFY,
    method: "POST",
    data: {
      from: data?.dateFrom,
      product: "HOTEL-DOMESTIC",
      property_id: data?.hotelId,
      room_id: data?.roomId,
      to: data?.dateTo,
      type: "AVAILABILITY"
    }
  }).then(({ data }) => ({ message: data?.message }));

export const fetchRemoveRoomNotify = (data: {
  dateFrom: string;
  hotelId: number;
  roomId: number;
  dateTo: string;
}) =>
  fetcher({
    url: ENDPOINTS.REMOVE_ROOM_NOTIFY,
    method: "POST",
    data: {
      from: data?.dateFrom,
      product: "HOTEL-DOMESTIC",
      property_id: data?.hotelId,
      room_id: data?.roomId,
      to: data?.dateTo,
      type: "AVAILABILITY"
    }
  }).then(({ data }) => ({ message: data?.message }));

export const getServerSideProps: GetServerSideProps = async ({
  query,
  res
}) => {
  const hotelData = await fetchHotelData(
    query.cityTitle,
    query.hotelTitle
  ).catch(() => ({ error: true }));

  if ("error" in hotelData) {
    res.statusCode = 410;
    return { props: {} };
  }

  const [seoData, hotelRateData, hotelPriceRange, hotelRecommendations] =
    await Promise.all([
      isChannel(["website", "web", "snapp-ir"])
        ? fetchSeoData(
            `${getOrigin()}${
              process.env.NEXT_PUBLIC_BASE_PATHNAME
            }/${query?.cityTitle}/${query?.hotelTitle}`,
            hotelData.seo_title ||
              `رزرو ${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title} با تضمین بهترین قیمت`,
            `رزرو ${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title} بهمراه مشخصات، کمترین قیمت، عکس اتاق ها، نظر کاربران و تخفیف ویژه در ${replaceSnappTripBasedOnChannel(
              replaceSnappTripBasedOnChannel("اسنپ تریپ")
            )}. ${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title} معرفی امکانات اتاق ها و پشتیبانی 24 ساعته.`
          )
        : Promise.resolve(null),
      fetchHotelRateData(hotelData?.id).catch(() => null),
      fetchHotelPriceRange(hotelData?.id),
      fetchHotelsData(hotelData?.city_id, query, hotelData?.stars, true)
    ]);

  if (seoData?.redirect) {
    return { redirect: { destination: seoData?.redirect, permanent: true } };
  }

  return {
    props: {
      seoData,
      hotelData,
      hotelRateData,
      hotelPriceRange,
      hotelRecommendations:
        hotelRecommendations?.filter(
          (item) => item.id !== hotelData.id && item.hotelDetail?.isAvailable
        ) ?? [],
      cancellationPoliciesData: {
        hotelRules: [
          "کودکان زیر دو سال میتوانند در صورت عدم استفاده از سرویس خواب اضافه به صورت رایگان به همراه والدین در هتل اقامت داشته باشند",
          ...(hotelData?.hotel_policy?.split(".") ?? [])
        ],
        cancellationRules: [
          `قوانین کنسلی ممکن است بر اساس نوع اتاق متفاوت باشد. برای مشاهده قوانین هر اتاق، به بخش "جزئیات بیشتر و قوانین کنسلی" مراجعه کنید.`
        ]
      }
    }
  };
};
